<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { page } from '$app/state';

	import {
		binToHex,
		cashAddressToLockingBytecode,
		encodeTransactionBch,
		hash256,
		hexToBin,
		sha256,
		swapEndianness,
		utf8ToBin
	} from '@bitauth/libauth';

	import Photon, { PHOTON_CATEGORY, tPHOTON_CATEGORY } from '@unspent/photon';
	import { ElectrumClient, ConnectionStatus } from '@electrum-cash/network';

	import Readme from './README.md';
	import BitauthLink from '$lib/BitauthLink.svelte';
	import ExplorerLinks from "$lib/ExplorerLinks.svelte";
	import CONNECTED from '$lib/images/connected.svg';
	import DISCONNECTED from '$lib/images/disconnected.svg';
	import Countdown from '$lib/Countdown.svelte';
	import Loading from '$lib/Loading.svelte';

	import {
		binToBigIntUint256LE,
		getHdPrivateKey,
		getScriptHash,
		sumUtxoValue,
		sumTokenAmounts,
		type UtxoI,
		sleep
	} from '@unspent/tau';

	import { IndexedDBProvider } from '@mainnet-cash/indexeddb-storage';
	import { BaseWallet, Wallet, TestNetWallet } from '@unspent/wallet';

	import BCH from '$lib/images/BCH.svg';
	import tBCH from '$lib/images/tBCH.svg';
	import tPHOTON from '$lib/images/tPHOTON.svg';
	import PHOTON from '$lib/images/PHOTON.svg';

	let workers: Worker[] = $state([]);
	let result;
	let workerStatus = $state('STATUS_IDLE');
	let workerMessage;
	let showSettings = $state(false);

	let now = $state(0);
	let baton: UtxoI = $state();
	let contractState = '';
	let target: string = $state();
	let walletScriptHash = $state('');
	let wallet: any;
	let miner: any;
	let walletUnspent: any[] = $state([]);
	let unspent: any[] = $state([]);
	let minerThrowawayKey = $state('');
	let walletKey = $state('');

	let hashRate = $state(0);
	let sumWalletTokens = $state(0n);
	let sumWallet = $state(0);
	let sumVaultTokens = $state(0n);
	let sumVault = $state(0);

	let connectionStatus = $state('');
	let electrumClient: any;
	let scripthash = '';
	scripthash = Photon.getScriptHash();
	const isMainnet =  page.url.hostname == 'vox.cash';
	let server = isMainnet ? 'electrum.imaginary.cash' : 'chipnet.bch.ninja';
	const icon = isMainnet ? PHOTON : tPHOTON;
	const CATEGORY = isMainnet ? binToHex(PHOTON_CATEGORY) : binToHex(tPHOTON_CATEGORY);
	const baseTicker = isMainnet ? 'BCH' : 'tBCH';
	const ticker = isMainnet ? 'PHOTON' : 'tPHOTON';
	const prefix = isMainnet ? 'bitcoincash' : 'bchtest';
	const bchIcon = isMainnet ? BCH : tBCH;
	const fee = isMainnet ? 1 : 50;

	const handleNotifications = async function (data: any) {
		if (data.method === 'blockchain.headers.subscribe') {
			let d = data.params[0];
			now = d.height;
			if (workerStatus == 'STATUS_MINING') {
				console.log("halting to update age")
				await halt();
				await sleep(1000);
				await mine();
			}
		} else if (data.method === 'blockchain.scripthash.subscribe') {
			if (data.params[1] !== contractState) {
				contractState = data.params[1];
				connectionStatus = ConnectionStatus[electrumClient.status];
				updateUnspent();
				updateWallet();
			}
		} else {
			console.log(data);
		}
	};

	const halt = async function () {
		workerStatus = 'STATUS_HALTED';
		workers.map((w) => w.terminate());
		await sleep(500);
		await initWebWorker();
		await sleep(100)
	};

	

	const mine = async function () {
		
		let lockingBytecodeResponse = cashAddressToLockingBytecode(wallet.getTokenDepositAddress())
	    if(typeof lockingBytecodeResponse == "string") throw lockingBytecodeResponse
		
		let template = Photon.generateTemplate(
			now,
			baton,
			minerThrowawayKey,
			lockingBytecodeResponse.bytecode,
			CATEGORY
		);
		if (window.Worker) {
			workerStatus = 'STATUS_MINING';
			workers.map((w) =>
				w.postMessage({ task: 'START', template: template, key: minerThrowawayKey })
			);
		} else {
			console.log('Start mining called before worker init.');
		}
	};

	const topUp1M = async function () {
		let tx = Photon.topUp(1_000_000, baton, walletUnspent, walletKey);
		let transaction_hex = binToHex(encodeTransactionBch(tx.transaction));
		let response = await broadcast(transaction_hex);
	};

	const broadcast = async function (raw_tx: string) {
		let response = await electrumClient.request('blockchain.transaction.broadcast', raw_tx);
		if (response instanceof Error) {
			connectionStatus = ConnectionStatus[electrumClient.status];
			throw response;
		}
		response as any[];
	};

	const fundVault = async function () {
		await wallet.sendMax(wallet.getDepositAddress());
		let id = await wallet.tokenGenesis({
			cashaddr: Photon.getAddress(prefix), // token UTXO recipient, if not specified will default to sender's address
			amount: BigInt(21e14), // fungible token amount
			value: 50000000n, // Satoshi value
			nft: {
				capability: 'mutable',
				commitment: '00000000fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff000'
			}
		});
		console.log(id);

		//
	};

	const updateWallet = async function () {
		let response = await electrumClient.request(
			'blockchain.scripthash.listunspent',
			walletScriptHash,
			'include_tokens'
		);
		if (response instanceof Error) throw response;

		walletUnspent = response;
		sumWallet = sumUtxoValue(walletUnspent, true);
		sumWalletTokens = sumTokenAmounts(walletUnspent, CATEGORY);

		walletUnspent = walletUnspent.filter((u: UtxoI) => !u.token_data);
	};

	const updateUnspent = async function () {
		let response = await electrumClient.request(
			'blockchain.scripthash.listunspent',
			scripthash,
			'include_tokens'
		);
		if (response instanceof Error) throw response;
		response = response
			.filter((u: UtxoI) => u.token_data?.category == CATEGORY)
			.filter((u: UtxoI) => u.token_data?.nft?.capability == 'mutable');
		if (response.length == 1) {
			let nextBaton = response[0] as UtxoI;
			if (!baton) baton = nextBaton;
			if (nextBaton && nextBaton.token_data) {
				if (nextBaton.token_data?.nft?.commitment !== baton.token_data?.nft?.commitment) {
					baton = nextBaton;
					if (workerStatus == 'STATUS_MINING') {
						console.log("halting to update contract state")
						await halt();
						await sleep(1000);
						await mine();
					}
				}
			}
			target = swapEndianness(baton.token_data?.nft?.commitment.slice(8, 72)!);
		}
		unspent = response;
		sumVault = sumUtxoValue(response, true);
		sumVaultTokens = sumTokenAmounts(response, CATEGORY);
	};

	async function initWebWorker() {
		// This function initiates the web worker
		// Check if we are in a browser
		if (window.Worker) {
			// Check if the browser supports web worker
			// We reset some values we use to visualise the progress
			workerStatus = 'STATUS_IDLE';
			result = undefined;
			// This is where we load the worker
			const MineWorker = await import('$workers/photon.js?worker');
			// And initiate the worker
			const CONNCURRENCY = 1 //navigator.hardwareConcurrency-1
			for (let i = 0; i < CONNCURRENCY ; i++) {
				workers[i] = new MineWorker.default();
				// The following part is called when the worker sends a message
				workers[i].onmessage = function (e: any) {
					// Let’s first get the status and the message from the event’s data
					const { status, message } = e.data;
					// We use these two variables on the website
					if (message) {
						workerMessage = message;
					}
					// if (status) {
					// 	workerStatus = status;
					// }
					// This checks what the status of the message is
					switch (status) {
						case 'STATUS_BROADCAST':
							// Broadcast the result returned from the web worker
							result = e.data.result;
							try {
								broadcast(result);
							} catch (e) {
								console.error(e);
							}
							// wait for the transaction to propogate.
							baton = Photon.getNextBatonUtxo(result);
							mine();
							break;
						case 'STATUS_HEARTBEAT':
							console.log(e.data);
							if (hashRate == 0) {
								hashRate = e.data.hashrate*CONNCURRENCY;
							} else {
								hashRate = Math.round((1 * hashRate / 2)  + (e.data.hashrate*(CONNCURRENCY) / 2));
							}
							break;
						case 'STATUS_MINING':
							console.log(e.data.message);
							break;
						default:
							console.log('default:', e);
					}
				};
			}
		} else {
			console.error('no worker');
		}
	}

	onMount(async () => {
		BaseWallet.StorageProvider = IndexedDBProvider;
		wallet = isMainnet ? await Wallet.named(`vox`) : await TestNetWallet.named(`vox`);
		miner = isMainnet ? await Wallet.named(`miner`) : await TestNetWallet.named(`miner`);
		walletKey = getHdPrivateKey(
			wallet.mnemonic!,
			wallet.derivationPath.slice(0, -2),
			wallet.isTestnet
		);
		minerThrowawayKey = getHdPrivateKey(
			miner.mnemonic!,
			miner.derivationPath.slice(0, -2),
			miner.isTestnet
		);
		let lockingCodeResult = cashAddressToLockingBytecode(wallet.getDepositAddress());
		if (typeof lockingCodeResult == 'string') throw lockingCodeResult;
		walletScriptHash = getScriptHash(lockingCodeResult.bytecode);

		// Initialize an electrum client.
		electrumClient = new ElectrumClient(Photon.USER_AGENT, '1.4.1', server);

		// Wait for the client to connect.
		await electrumClient.connect();
		// Set up a callback function to handle new blocks.

		// Listen for notifications.
		electrumClient.on('notification', handleNotifications);

		// Set up a subscription for new block headers.
		await electrumClient.subscribe('blockchain.scripthash.subscribe', scripthash);
		await electrumClient.subscribe('blockchain.scripthash.subscribe', walletScriptHash);
		await electrumClient.subscribe('blockchain.headers.subscribe');

		updateUnspent();
		updateWallet();
		initWebWorker();
	});

	onDestroy(async () => {
		workers.map((w) => {
			w.terminate();
		});
		await electrumClient.disconnect();
	});
</script>

<svelte:head>
	<title>γ Photons</title>
	<meta name="description" content="Emit Photons Tokens" />
</svelte:head>

<section>
	<div class="status-bar">
		<ExplorerLinks address={Photon.getAddress(prefix)} {isMainnet}></ExplorerLinks>

	<div class="status">
		{now.toLocaleString()}<sub>■</sub>
		<BitauthLink template={Photon.template} />
		{#if connectionStatus == 'CONNECTED'}
			<img src={CONNECTED} alt={connectionStatus} />
		{:else}
			<img src={DISCONNECTED} alt="Disconnected" />
		{/if}
	</div>
	</div>

	<h1>Capture Photons</h1>
	<div class="swap">
		<div>
			<img width="50" src={icon} alt={ticker} />
			<br />
			{(sumWalletTokens / 100_000_000n).toLocaleString(undefined, { maximumFractionDigits: 5 })}
			{ticker}
		</div>
	</div>

	<div class="mining">
		{#if baton}
			<button class="button" disabled={workerStatus == 'STATUS_MINING'} onclick={() => mine()}
				>go
			</button>
			<button disabled={workerStatus == 'STATUS_IDLE'} class="button" onclick={() => halt()}
				>stop
			</button>
		{/if}
		{#if hashRate}
			<p>{hashRate} Hash/s</p>
		{/if}
		<p>{workerStatus}</p>
	</div>

	{#if baton && baton.value > 0}
		<h3>Vault Status</h3>

		<div class="swap">
			<div>
				<img width="50" src={icon} alt={ticker} />
				<br />
				{(sumVaultTokens / 100_000_000n).toLocaleString()}
				{ticker}<br />
				{(sumVault / 100_000_000).toLocaleString()}
				{baseTicker}
				<img width="18px" src={bchIcon} alt={baseTicker} />
			</div>
		</div>
		<h4>Current Difficulty</h4>
		<p>Current Target</p>
		<pre>{binToHex(Photon.getNextTarget(baton, now))}</pre>
		<p>Previous Target</p>
		<pre>{swapEndianness(target)}</pre>
		Height: {baton.height} <br />
		Next Payout: {(
			Number(BigInt(baton.token_data?.amount!) / 420000n) / 100_000_000
		).toLocaleString(undefined, { maximumFractionDigits: 5 })}
		{ticker}<br />
		Cash: {baton.value.toLocaleString()} sats {baseTicker}<br />

		<p>{ticker} category:</p>
		<pre>{baton.token_data?.category}</pre>

		<h3>Advanced</h3>

		<label class="switch">
			<input type="checkbox" bind:checked={showSettings} />
			<span class="slider round"></span>
		</label>
	{:else if !isMainnet}
		<button class="button" onclick={() => fundVault()}
			>Mint Chipnet Genesis Tx (0.5 {baseTicker})</button
		>
	{:else if Date.now() < 1791023920000}
		<div class="swap">
			<Countdown end={1791023920000} />
		</div>
	{:else}
		<div class="swap">
			<Loading />
			<p>awaiting baton</p>
		</div>
	{/if}

	{#if showSettings}
		<button class="button" onclick={() => topUp1M()}>Donate 1M sats to mining baton. </button>
	{/if}

	<Readme />
</section>

<style>
	pre {
		font-size: x-small;
	}

	.status-bar {
		display: flex;
		justify-content: space-between;
	}

	.status {
		text-align: end;
		color: #ffffff;
		font-weight: 600;
	}

	.button:hover {
		background-color: #a991af;
	}

	.mining {
		text-align: center;
	}
	.swap {
		display: flex;
		margin: auto;
		align-items: center;
		justify-content: center;
	}
	.swap div {
		padding: 10px;
		justify-content: center;
		text-align: center;
	}

	.switch {
		position: relative;
		display: inline-block;
		width: 60px;
		height: 34px;
	}

	/* Hide default HTML checkbox */
	.switch input {
		opacity: 0;
		width: 0;
		height: 0;
	}

	/* The slider */
	.slider {
		position: absolute;
		cursor: pointer;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: #ccc;
		-webkit-transition: 0.4s;
		transition: 0.4s;
	}

	.slider:before {
		position: absolute;
		content: '';
		height: 26px;
		width: 26px;
		left: 4px;
		bottom: 4px;
		background-color: white;
		-webkit-transition: 0.4s;
		transition: 0.4s;
	}

	input:checked + .slider {
		background-color: #a45eb6;
	}

	input:focus + .slider {
		box-shadow: 0 0 1px #a45eb6;
	}

	input:checked + .slider:before {
		-webkit-transform: translateX(26px);
		-ms-transform: translateX(26px);
		transform: translateX(26px);
	}

	/* Rounded sliders */
	.slider.round {
		border-radius: 34px;
	}

	.slider.round:before {
		border-radius: 50%;
	}
</style>
